/**
 * Copilot Command Palette — Press '/' anywhere
 * ═══════════════════════════════════════════════════════════════
 * Context-aware command suggestions based on current page.
 */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCopilot } from './CopilotProvider';
import { Sparkles, Search, ArrowRight, FileText, BarChart3, Users, Calendar, Zap, MessageSquare } from 'lucide-react';

const COMMANDS = {
  // HiveLink publishes the channel, its membership and the recent tail via
  // publishEntity, so these commands have real material to work with rather
  // than only knowing which page you are on.
  hivelink: [
    { cmd: 'summarise this channel', icon: FileText, label: 'Summarise this channel' },
    { cmd: 'what did I miss', icon: Zap, label: 'What did I miss here?' },
    { cmd: 'extract decisions', icon: FileText, label: 'Extract decisions from this channel' },
    { cmd: 'extract action items', icon: Zap, label: 'Turn this thread into tasks' },
    { cmd: 'who should I ask', icon: Users, label: 'Who here knows about this?' },
  ],
  dashboard: [
    { cmd: 'show overdue tasks', icon: Zap, label: 'Show overdue tasks' },
    { cmd: 'summarize today', icon: FileText, label: 'Summarize today\'s activity' },
    { cmd: 'team availability', icon: Users, label: 'Check team availability' },
  ],
  task: [
    { cmd: 'generate description', icon: FileText, label: 'Generate task description' },
    { cmd: 'suggest assignee', icon: Users, label: 'Suggest best assignee' },
    { cmd: 'estimate effort', icon: BarChart3, label: 'Estimate story points' },
    { cmd: 'find similar tasks', icon: Search, label: 'Find similar tasks' },
  ],
  project: [
    { cmd: 'project health summary', icon: BarChart3, label: 'Project health summary' },
    { cmd: 'risk analysis', icon: Zap, label: 'Identify risks' },
    { cmd: 'generate status report', icon: FileText, label: 'Generate status report' },
  ],
  sprint: [
    { cmd: 'sprint velocity trend', icon: BarChart3, label: 'Show velocity trend' },
    { cmd: 'scope creep check', icon: Zap, label: 'Check for scope creep' },
    { cmd: 'suggest sprint goal', icon: FileText, label: 'Suggest sprint goal' },
  ],
  budget: [
    { cmd: 'budget variance report', icon: BarChart3, label: 'Budget variance analysis' },
    { cmd: 'forecast remaining', icon: FileText, label: 'Forecast remaining budget' },
    { cmd: 'top expenses', icon: Zap, label: 'Show top expense categories' },
  ],
  invoice: [
    { cmd: 'overdue invoices', icon: Zap, label: 'List overdue invoices' },
    { cmd: 'generate invoice', icon: FileText, label: 'Draft a new invoice' },
    { cmd: 'payment follow-up', icon: MessageSquare, label: 'Draft payment reminder' },
  ],
  calendar: [
    { cmd: 'prepare meeting agenda', icon: FileText, label: 'Prepare meeting agenda' },
    { cmd: 'find free slot', icon: Calendar, label: 'Find free time slot' },
    { cmd: 'summarize upcoming week', icon: BarChart3, label: 'Upcoming week summary' },
  ],
};

const GLOBAL_COMMANDS = [
  { cmd: 'navigate to projects', icon: ArrowRight, label: 'Go to Projects' },
  { cmd: 'navigate to tasks', icon: ArrowRight, label: 'Go to Tasks' },
  { cmd: 'navigate to studio', icon: Sparkles, label: 'Open Zuno Studio' },
  { cmd: 'explain this page', icon: Search, label: 'Explain current page' },
];

export default function CopilotCommandPalette() {
  const { commandPaletteOpen, setCommandPaletteOpen, pageContext, assist } = useCopilot();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (commandPaletteOpen) {
      setQuery(''); setResult(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [commandPaletteOpen]);

  const contextCmds = COMMANDS[pageContext.type] || COMMANDS.dashboard;
  const allCmds = [...contextCmds, ...GLOBAL_COMMANDS];
  const filtered = query ? allCmds.filter(c => c.label.toLowerCase().includes(query.toLowerCase()) || c.cmd.includes(query.toLowerCase())) : contextCmds;

  const executeCommand = async (cmd) => {
    if (cmd.startsWith('navigate to ')) {
      const target = cmd.replace('navigate to ', '').trim();
      navigate(`/${target}`);
      setCommandPaletteOpen(false);
      return;
    }
    setLoading(true);
    const res = await assist('command', { context: cmd });
    setResult(res);
    setLoading(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (query.trim()) executeCommand(query.trim());
  };

  if (!commandPaletteOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[9998]" onClick={() => setCommandPaletteOpen(false)} />
      <div className="fixed top-[20%] left-1/2 -translate-x-1/2 w-[560px] bg-white rounded-2xl shadow-2xl border border-gray-200 z-[9999] overflow-hidden">
        {/* Input */}
        <form onSubmit={handleSubmit} className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
          <Sparkles size={18} className="text-blue-500 shrink-0" />
          <input ref={inputRef} type="text" value={query} onChange={e => setQuery(e.target.value)}
            placeholder={`Ask anything about ${pageContext.label}...`}
            className="flex-1 text-sm outline-none placeholder-gray-400" autoFocus />
          {loading && <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />}
        </form>

        {/* Results */}
        {result ? (
          <div className="px-5 py-4 max-h-60 overflow-y-auto">
            <div className="text-xs text-gray-600 whitespace-pre-wrap leading-relaxed">{result}</div>
            <button onClick={() => setResult(null)} className="mt-3 text-[10px] text-blue-500 hover:text-blue-700">Back to commands</button>
          </div>
        ) : (
          <div className="max-h-72 overflow-y-auto py-2">
            {filtered.length === 0 && query && (
              <div className="px-5 py-3 text-xs text-gray-400">Press Enter to ask: "{query}"</div>
            )}
            {filtered.map((cmd, i) => {
              const Icon = cmd.icon;
              return (
                <button key={i} onClick={() => executeCommand(cmd.cmd)}
                  className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-blue-50 transition-colors">
                  <div className="w-7 h-7 rounded-lg bg-gray-50 flex items-center justify-center shrink-0">
                    <Icon size={14} className="text-gray-400" />
                  </div>
                  <span className="text-sm text-gray-700">{cmd.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Footer */}
        <div className="px-5 py-2.5 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
          <span>Press <kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] font-mono">/</kbd> to open</span>
          <span><kbd className="px-1 py-0.5 bg-gray-100 rounded text-[9px] font-mono">Esc</kbd> to close</span>
        </div>
      </div>
    </>
  );
}